import {
  Box,
  Flex,
  Text,
  Table,
  Thead,
  Th,
  Tr,
  Tbody,
  Td
} from '@chakra-ui/react';
import { faTriangleExclamation } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Banner,
  Button,
  ButtonText,
  useToast,
  Badge
} from '@morressier/alembic';
import {
  ISubmissionContextConfig,
  IAuthorICMJEFormList
} from '@publishing-engine/domain/models';
import { useState } from 'react';
import ProtectedAction from 'src/components/ProtectedAction';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import { downloadFile } from 'src/utils/file';
import { authorSubmitAllICMJEForm } from '../../../../../api/queries';
import { AuthorUploadICMJEFormForAll } from './AuthorUploadICMJEFormForAll';
import { formatDate } from '../../../../../utils/date';
import ProtectedActionButton from '../../../../ProtectedAction/Button';
import { AuthorReplaceICMJEFormModal } from './AuthorReplaceICMJEForm';

type Props = {
  config: ISubmissionContextConfig;
};

const SubmittingAuthoICMJEFormForAll: React.FC<Props> = ({ config }) => {
  const {
    submissionId,
    submissionContextId,
    organizationId,
    icmjeFormList,
    refetchSubmission
  } = useSubmissionData();

  const toast = useToast();

  const [activeEmail, setActiveEmail] = useState<string>('');
  const [activeSubmittingAuthorStatus, setActiveSubmittingAuthorStatus] =
    useState<boolean>(false);

  const isDisable = !(
    icmjeFormList?.every(author => author.isUploaded === true) ?? false
  );

  const [isUploadICMJEFormForAll, setIsUploadICMJEFormForAll] =
    useState<boolean>(false);

  const [isReplaceFormModalEnabled, setIsReplaceFormModalEnabled] =
    useState<boolean>(false);

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const onSubmit = async () => {
    setIsSubmitting(true);
    try {
      await authorSubmitAllICMJEForm(submissionId, 'author');
      setIsSubmitting(false);
      toast({
        variant: 'success',
        title: 'All Forms and Disclosures submitted successfully'
      });
      refetchSubmission();
    } catch (err) {
      setIsSubmitting(false);
      toast({
        variant: 'error',
        title: 'Failed to submit Forms and Disclosures'
      });
    }
  };

  const [isFileDownloading, setIsFileDownloading] = useState(false);

  const handleDownload = async (fileUrl: string) => {
    try {
      setIsFileDownloading(true); // disable button
      await downloadFile(fileUrl);
    } finally {
      setIsFileDownloading(false); // re-enable no matter what
    }
  };

  const handleReplaceModal = (author: IAuthorICMJEFormList) => {
    setActiveSubmittingAuthorStatus(author.isSubmittingAuthor);
    setActiveEmail(author.email);
    if (author.isUploaded) {
      setIsReplaceFormModalEnabled(true);
    } else {
      setIsUploadICMJEFormForAll(true);
    }
  };

  return (
    <Flex flexDirection="column" gap={8}>
      <Box>
        <Flex flexDirection="column" gap={8}>
          <Flex flexDirection="column" gap={8}>
            <Flex flexDirection="column" gap={2}>
              <Text textStyle="headingSection">Forms and Disclosures</Text>
              <Text textStyle="textBody">
                As the submitting author, please download and upload the
                required form for this submission:
              </Text>
            </Flex>
            <Banner
              heading=""
              content={
                <Flex flexDirection="row">
                  <Text textStyle="headingItem">
                    Download The Forms and Disclosures First:{' '}
                  </Text>
                  <ButtonText
                    variant="primary"
                    onClick={() => {
                      if (config.authorICMJEFormUrl) {
                        handleDownload(config.authorICMJEFormUrl);
                      }
                    }}
                    isDisabled={isFileDownloading}
                  >
                    Download Forms and Disclosures
                  </ButtonText>
                </Flex>
              }
              customIcon={<FontAwesomeIcon icon={faTriangleExclamation} />}
              variant="attention"
            />
          </Flex>

          <Box mx={-16}>
            <Box px={16}>
              <Flex flexDirection={'column'}>
                <Table>
                  <Thead>
                    <Tr
                      gap={4}
                      sx={{
                        '& th': {
                          textTransform: 'none',
                          color: 'black'
                        }
                      }}
                    >
                      <Th>
                        <Text textStyle="headingItem" whiteSpace="nowrap">
                          Author Name
                        </Text>
                      </Th>
                      <Th>
                        <Text textStyle="headingItem" whiteSpace="nowrap">
                          Status
                        </Text>
                      </Th>
                      <Th></Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {icmjeFormList?.map((author, i) => (
                      <Tr key={i} gap={4} background="white">
                        <Td>
                          <Text textStyle="headingLine" whiteSpace="normal">
                            {author.name}
                          </Text>
                          <Text>{author.email}</Text>
                          {author.isSubmittingAuthor ? (
                            <Badge size="context" variant="attention">
                              Submitting Author
                            </Badge>
                          ) : null}
                        </Td>
                        <Td>
                          {author.isUploaded ? (
                            <Text>
                              <Badge size="context" variant="success">
                                Uploaded
                              </Badge>
                              {` on ${formatDate(new Date(author.date))}`}
                            </Text>
                          ) : (
                            <Badge size="context" variant="attention">
                              Awaiting Upload
                            </Badge>
                          )}
                        </Td>
                        <Td>
                          {author.isUploaded ? (
                            <Flex gap={2}>
                              <ProtectedActionButton
                                relation="author"
                                entityType="submission"
                                entityId={submissionId}
                                variant="secondary"
                                as="text"
                                onClick={() => handleDownload(author.file.url)}
                                tooltipLabel={
                                  <span>
                                    You do not have permission to perform this
                                    action
                                  </span>
                                }
                              >
                                Download
                              </ProtectedActionButton>
                              <ProtectedActionButton
                                relation="author"
                                entityType="submission"
                                entityId={submissionId}
                                variant="secondary"
                                as="text"
                                onClick={() => handleReplaceModal(author)}
                                tooltipLabel={
                                  <span>
                                    You do not have permission to perform this
                                    action
                                  </span>
                                }
                              >
                                Replace
                              </ProtectedActionButton>
                            </Flex>
                          ) : (
                            <ProtectedActionButton
                              relation="author"
                              entityType="submission"
                              entityId={submissionId}
                              variant="secondary"
                              as="text"
                              onClick={() => {
                                setActiveSubmittingAuthorStatus(
                                  author.isSubmittingAuthor
                                );
                                setActiveEmail(author.email);
                                setIsUploadICMJEFormForAll(true);
                              }}
                              tooltipLabel={
                                <span>
                                  You do not have permission to perform this
                                  action
                                </span>
                              }
                            >
                              Upload
                            </ProtectedActionButton>
                          )}
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Flex>
            </Box>
          </Box>

          {isUploadICMJEFormForAll && (
            <AuthorUploadICMJEFormForAll
              onClose={() => setIsUploadICMJEFormForAll(false)}
              submissionId={submissionId}
              organizationId={organizationId}
              submissionContextId={submissionContextId}
              authorEmail={activeEmail}
              authorStatus={activeSubmittingAuthorStatus}
              icmjeFormList={icmjeFormList ?? []}
            />
          )}

          {isReplaceFormModalEnabled && (
            <AuthorReplaceICMJEFormModal
              onSubmit={() => {
                setIsReplaceFormModalEnabled(false);
                setIsUploadICMJEFormForAll(true);
              }}
              onClose={() => setIsReplaceFormModalEnabled(false)}
            />
          )}

          <Flex width="100%" justifyContent="flex-start" gap={4}>
            <ProtectedAction
              relation="author"
              entityType="submission"
              entityId={submissionId}
            >
              <Button
                variant="secondary"
                onClick={() => onSubmit()}
                disabled={isDisable || isSubmitting}
                isLoading={isSubmitting}
              >
                Submit
              </Button>
            </ProtectedAction>
          </Flex>
        </Flex>
      </Box>
    </Flex>
  );
};

export default SubmittingAuthoICMJEFormForAll;
